import { Filter, FolderPlus, Plus, Search } from 'lucide-react'
import { cn } from '../../lib/utils'

interface ContextualSidebarToolbarProps {
  isDark: boolean
  search: string
  showBrowse: boolean
  createDocumentPending: boolean
  createFolderPending: boolean
  canCreate: boolean
  onSearchChange: (value: string) => void
  onNewDocument: () => void
  onNewFolder: () => void
  onToggleBrowse: () => void
}

export function ContextualSidebarToolbar({
  isDark,
  search,
  showBrowse,
  createDocumentPending,
  createFolderPending,
  canCreate,
  onSearchChange,
  onNewDocument,
  onNewFolder,
  onToggleBrowse,
}: ContextualSidebarToolbarProps) {
  const inactiveButton = isDark
    ? 'text-neutral-400 hover:bg-surface-3 hover:text-neutral-200 active:bg-surface-3'
    : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 active:bg-neutral-100'

  return (
    <>
      <div className="px-2 py-2">
        <label className="relative block">
          <span className="sr-only">Filter documents</span>
          <Search
            className={cn(
              'absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2',
              isDark ? 'text-neutral-500' : 'text-neutral-400',
            )}
            aria-hidden="true"
          />
          <input
            type="search"
            placeholder="Filter documents…"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            className={cn(
              'w-full rounded-lg border py-2 pl-8 pr-3 text-base outline-none md:py-1.5 md:text-xs',
              isDark
                ? 'border-white/[0.06] bg-surface-2 text-neutral-200 placeholder-neutral-500 focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30'
                : 'border-neutral-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30',
            )}
          />
        </label>
      </div>

      <div className="flex gap-1 px-2 py-1">
        {canCreate && (
          <button
            type="button"
            onClick={onNewDocument}
            disabled={createDocumentPending}
            className={cn(
              'flex flex-1 items-center gap-1.5 rounded-lg px-2 py-2.5 text-sm font-medium md:py-1.5 md:text-xs',
              inactiveButton,
            )}
          >
            <Plus className="h-4 w-4 md:h-3.5 md:w-3.5" aria-hidden="true" />
            New Manual document
          </button>
        )}
        {canCreate && (
          <button
            type="button"
            onClick={onNewFolder}
            disabled={createFolderPending}
            className={cn('rounded-lg px-2 py-2.5 md:py-1.5', inactiveButton)}
            title="New folder"
            aria-label="New folder"
          >
            <FolderPlus className="h-4 w-4 md:h-3.5 md:w-3.5" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          onClick={onToggleBrowse}
          className={cn(
            'rounded-lg px-2 py-2.5 md:py-1.5',
            showBrowse ? 'bg-brand-600 text-white' : inactiveButton,
          )}
          title="Filter by tags and categories"
          aria-label="Filter by tags and categories"
          aria-expanded={showBrowse}
        >
          <Filter className="h-4 w-4 md:h-3.5 md:w-3.5" aria-hidden="true" />
        </button>
      </div>
    </>
  )
}
